import { toast } from "react-toastify";
import type { GetReviewByStoreIdQuery } from "../../../__generated__/graphql";
import { Review, Store } from "../../../__generated__/graphql";
import { isApp, isIOSApp } from "../../../utils/user-agent";
import { useUser } from "../../../core/user-context";
import { LuShare } from "react-icons/lu";
import { HiOutlineXMark } from "react-icons/hi2";
import TransitionWrapper from "../../../components/common/transition-wrapper";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import heroGray from "../../../assets/images/hero_gray.png";
import { useState } from "react";
import { useQuery } from "@apollo/client";
import { GET_REVIEW_BY_STORE_ID } from "../../../api/store-review-queries";
import { track } from "@amplitude/analytics-browser";

interface ReferralShareButtonProps {
  store: Store; // 공유할 매장
  wrapperClassName?: string;
  onClick: () => void;
}

export const ReferralShareButton: React.FC<ReferralShareButtonProps> = ({
  store,
  wrapperClassName,
  onClick,
}: ReferralShareButtonProps) => {
  const { user } = useUser();
  const [myReviews, setMyReviews] = useState<Review[] | null>(null); // 내가 작성한 리뷰
  const [shareWithMyReviewModalOpen, setShareWithMyReviewModalOpen] =
    useState(false); // 내가 작성한 리뷰 공유 모달 열기

  useQuery(GET_REVIEW_BY_STORE_ID, {
    fetchPolicy: "cache-first", // 이미 불러옴
    variables: {
      storeId: store.id,
    },
    onCompleted: (data) => {
      setMyReviews(
        // 내가 작성한 리뷰만 필터링
        data.getReviewByStoreId.filter(
          (review: GetReviewByStoreIdQuery["getReviewByStoreId"][number]) =>
            review.user?.id === user?.id
        ) as Review[]
      );
    },
  });

  // 로그인하지 않았을 때 공유 메시지
  const copyMessageNoUser = 
`[${store?.title}] ${store?.address}
https://mahi.co.kr/store/${store?.id}`; // prettier-ignore

  const copyMessageHasUser = 
`[${store?.title}] ${store?.address}
https://mahi.co.kr/store/${store?.id}

제가 좋아하는 가게, 같이 나눠요 🤝 
가입 후 코드 입력하면 우리 둘 다 1OOO원 쿠폰을 받아요.

${user?.name || "히어로"}님의 추천인 코드: ${user?.referralCode}`; // prettier-ignore

  /**
   * 공유 버튼 클릭 시 공유 메시지 생성
   */
  const handleShareClick = () => {
    onClick();

    // 로그인하지 않았어도 공유는 가능
    if (!user) {
      navigator.clipboard.writeText(copyMessageNoUser);

      toast.info(
        <div style={{ whiteSpace: "pre-line" }}>
          <p>
            <span className="font-bold">{store?.title}</span> 페이지가
            복사되었어요.
          </p>
          <p>이제 친구에게 공유해보세요!</p>
          <p>혜택을 받기 위한 추천인 코드는 로그인이 필요해요.</p>
        </div>
      );
      return;
    }

    // 해당 매장에 내가 작성한 리뷰가 있을 경우
    if (myReviews && myReviews.length > 0) {
      setShareWithMyReviewModalOpen(true); // 내가 작성한 리뷰 선택 모달 열기
      return;
    }

    // 해당 매장에 내가 작성한 리뷰가 없을 경우
    // ios는 모바일에서 네이티브 공유 기능 사용
    if (isApp() && isIOSApp()) {
      navigator
        .share({
          text: copyMessageHasUser,
        })
        .then(() => {
          toast.success("공유가 완료되었어요!");
        })
        .catch(() => {
          // 사용자가 공유를 취소한 경우
        });
    } else {
      // 데스크톱에서는 클립보드 복사
      navigator.clipboard
        .writeText(copyMessageHasUser)
        .then(() => {
          toast.success(
            <div style={{ whiteSpace: "pre-line" }}>
              <p>
                <span className="font-bold">{store?.title}</span> 페이지와
              </p>
              <p>
                <span className="font-bold">{user?.name || "히어로"}</span>님의
                추천인 코드가 복사되었어요.
              </p>
              <p>이제 친구에게 공유해보세요!</p>
            </div>
          );
        })
        .catch(() => {
          toast.error("링크 복사에 실패했어요.");
        });
    }
  };

  /**
   * 내가 작성한 리뷰 선택 모달에서 리뷰 선택 시 공유 메시지 생성
   * @param selectedReview 선택한 리뷰
   */
  const handleReviewSelect = (selectedReview: Review) => {
    const reviewImageUrl =
      selectedReview.reviewImages && selectedReview.reviewImages.length > 0
        ? selectedReview.reviewImages[0]?.img
        : "";

    const copyMessageWithReview = 
`[${store?.title}] ${store?.address}
https://mahi.co.kr/store/${store?.id}

────── ✦ ──────
${selectedReview.content}
────── ✦ ──────

'${user?.name || "히어로"}'님이 직접 구매하고 쓴 찐후기에요!

가입 후 코드 입력하면 우리 둘 다 1OOO원 쿠폰을 받아요.

${user?.name || "히어로"}님의 추천인 코드: ${user?.referralCode}`; // prettier-ignore

    setShareWithMyReviewModalOpen(false);

    // ios는 모바일에서 네이티브 공유 기능 사용
    if (isApp() && isIOSApp()) {
      // 이미지가 있으면 파일로 변환해서 공유
      if (reviewImageUrl) {
        fetch(reviewImageUrl)
          .then((response) => response.blob())
          .then((blob) => {
            const file = new File([blob], "review-image.jpg", {
              type: blob.type,
            });
            const shareData: ShareData = {
              text: copyMessageWithReview,
              files: [file],
            };

            // navigator.canShare 메서드를 사용하여 공유 가능한지 확인
            if (navigator.canShare && navigator.canShare(shareData)) {
              return navigator.share(shareData);
            } else {
              // 파일 공유가 지원되지 않으면 텍스트만 공유
              return navigator.share({ text: copyMessageWithReview });
            }
          })
          .then(() => {
            toast.success("공유가 완료되었어요!");
          })
          .catch(() => {
            // 실패하면 텍스트만 공유
            navigator
              .share({ text: copyMessageWithReview })
              .then(() => toast.success("공유가 완료되었어요!"))
              .catch(() => {});
          });
      } else {
        // 이미지가 없으면 텍스트만 공유
        navigator
          .share({ text: copyMessageWithReview })
          .then(() => {
            toast.success("공유가 완료되었어요!");
          })
          .catch(() => {
            // 사용자가 공유를 취소한 경우
          });
      }
    } else {
      // 데스크톱에서는 클립보드 복사
      navigator.clipboard
        .writeText(copyMessageWithReview)
        .then(() => {
          toast.success(
            <div style={{ whiteSpace: "pre-line" }}>
              <p>
                <span className="font-bold">{store?.title}</span>에 작성한
                찐후기와
              </p>
              <p>
                <span className="font-bold">{user?.name || "히어로"}</span>님의
                추천인 코드가 복사되었어요.
              </p>
              <p>이제 친구에게 공유해보세요!</p>
            </div>
          );
        })
        .catch(() => {
          toast.error("링크 복사에 실패했어요.");
        });
    }
  };

  return (
    <>
      <TransitionWrapper
        scale={0.95}
        opacity={0.8}
        onClick={handleShareClick}
        className={`w-1/6 h-[60px] flex items-center justify-center rounded-lg border-2 bg-white ${wrapperClassName}`}
      >
        <div className="flex flex-col items-center animate-wiggle-once">
          <LuShare size={26} className="text-gray-900" />
          <div className="flex items-center bg-[#fd6180] border border-[#fd6180] rounded-full mt-0.5">
            <span className="text-[10px] text-[#fff4f6] font-bold px-[4px] py-[2px] leading-none whitespace-nowrap tracking-tighter">
              1,000원
            </span>
          </div>
        </div>
      </TransitionWrapper>

      {/* 내가 작성한 리뷰 선택 모달 */}
      {shareWithMyReviewModalOpen && myReviews && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-[90%] max-w-[500px] max-h-[90vh] relative">
            <div className="p-6 overflow-y-auto max-h-[90vh]">
              <div className="flex justify-between items-start mb-6">
                <div className="flex flex-col gap-y-2">
                  <h2 className="text-xl font-bold">
                    어떤 리뷰로 친구를 초대할까요?
                  </h2>
                  <p className="text-md text-gray-700 break-keep">
                    '{user?.name || "히어로"}'님이 경험한{" "}
                    <span className="font-bold">따뜻한 감동</span>을 그대로
                    전해봐요 ☺️
                  </p>
                </div>
                <button
                  onClick={() => setShareWithMyReviewModalOpen(false)}
                  className="text-gray-500 active:text-gray-700"
                >
                  <HiOutlineXMark className="w-6 h-6" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {myReviews.map((review) => (
                  <div
                    key={review.id}
                    onClick={() => {
                      track("내가 쓴 찐후기 공유하기 클릭", {
                        어디서: "매장 상세 페이지",
                      });
                      handleReviewSelect(review);
                    }}
                    className="aspect-square rounded-lg overflow-hidden relative cursor-pointer border-2 border-gray-200 hover:border-[#fd6180] transition-colors"
                  >
                    {review.reviewImages && review.reviewImages.length > 0 ? (
                      <ResponsiveCDNImage
                        src={review.reviewImages[0]?.img}
                        alt="리뷰 이미지"
                        className="w-full h-full object-cover"
                        sizeRatio={0.5}
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-50 flex items-center justify-center">
                        <img
                          src={heroGray}
                          alt="리뷰 이미지"
                          className="w-full h-[50px] object-contain rounded-lg"
                        />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                      <div className="text-white/90 line-clamp-2 text-sm">
                        {review.content}
                      </div>
                      <div className="text-white/70 text-xs mt-1">
                        {new Date(review.createdAt).toLocaleDateString(
                          "ko-KR",
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
